import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface DominantConfig {
  historySize: number;
  minHistoryForUpdate: number;
  updateInterval: number;
  dominanceThreshold: number;
  minRssiDifferenceToSwitch: number;
  scatterRadius: number;
  deterministicScatter: boolean;
  minConfidence: number;
  useRssiBasedDistance: boolean;
  rssiDistanceModel: string;
  minDistancePercent: number;
  maxDistancePercent: number;
  nearRssi: number;
  farRssi: number;
  useDirectionalPositioning: boolean;
  secondaryGatewayWeight: number;
  tertiaryGatewayWeight: number;
  minSecondaryRssiDiff: number;
}

interface DominantConfigEditorProps {
  password: string;
}

const DominantConfigEditor: React.FC<DominantConfigEditorProps> = ({
  password,
}) => {
  const [config, setConfig] = useState<DominantConfig | null>(null);
  const [defaults, setDefaults] = useState<DominantConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/dominant-monitoring/dominant-config`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setConfig(response.data.config);
        setDefaults(response.data.defaults);
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to fetch configuration",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/api/dominant-monitoring/dominant-config`,
        config,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setMessage({
          type: "success",
          text: "Configuration saved successfully. Changes will apply to new position calculations.",
        });
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to save configuration",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset all settings to defaults? This cannot be undone."
      )
    ) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/dominant-monitoring/dominant-config/reset`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setConfig(response.data.config);
        setMessage({
          type: "success",
          text: "Configuration reset to defaults successfully",
        });
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to reset configuration",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8 text-gray-500">
          Loading configuration...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              ⚙️ Dominant Positioning Configuration
            </h2>
            <p className="text-gray-600 mt-1">
              Adjust dominant mode positioning algorithm parameters
            </p>
          </div>
          <button
            onClick={fetchConfig}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            title="Refresh configuration"
          >
            <svg
              className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-3 rounded-md ${
              message.type === "success"
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <p
              className={`text-sm ${
                message.type === "success" ? "text-green-800" : "text-red-800"
              }`}
            >
              {message.text}
            </p>
          </div>
        )}
      </div>

      {/* Signal History Settings */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          📊 Signal History
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              History Size: {config.historySize}
              <span className="text-gray-500 ml-2">
                (Default: {defaults?.historySize})
              </span>
            </label>
            <input
              type="range"
              min="10"
              max="50"
              value={config.historySize}
              onChange={(e) =>
                setConfig({ ...config, historySize: parseInt(e.target.value) })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum number of signal readings to keep in FIFO history
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min History for Update: {config.minHistoryForUpdate}
              <span className="text-gray-500 ml-2">
                (Default: {defaults?.minHistoryForUpdate})
              </span>
            </label>
            <input
              type="range"
              min="5"
              max="30"
              value={config.minHistoryForUpdate}
              onChange={(e) =>
                setConfig({
                  ...config,
                  minHistoryForUpdate: parseInt(e.target.value),
                })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum signal history entries required before calculating
              position
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Update Interval: {config.updateInterval / 1000}s
              <span className="text-gray-500 ml-2">
                (Default: {(defaults?.updateInterval || 60000) / 1000}s)
              </span>
            </label>
            <input
              type="range"
              min="30"
              max="300"
              step="30"
              value={config.updateInterval / 1000}
              onChange={(e) =>
                setConfig({
                  ...config,
                  updateInterval: parseInt(e.target.value) * 1000,
                })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              How often to recalculate device position (in seconds)
            </p>
          </div>
        </div>
      </div>

      {/* Gateway Switching Behavior */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          🔄 Gateway Switching Behavior
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dominance Threshold:{" "}
              {(config.dominanceThreshold * 100).toFixed(1)}%
              <span className="text-gray-500 ml-2">
                (Default:{" "}
                {((defaults?.dominanceThreshold || 0.07) * 100).toFixed(1)}%)
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="50"
              step="1"
              value={config.dominanceThreshold * 100}
              onChange={(e) =>
                setConfig({
                  ...config,
                  dominanceThreshold: parseInt(e.target.value) / 100,
                })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum percentage of signals from dominant gateway required
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min RSSI Difference to Switch: {config.minRssiDifferenceToSwitch}{" "}
              dBm
              <span className="text-gray-500 ml-2">
                (Default: {defaults?.minRssiDifferenceToSwitch} dBm)
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="10"
              value={config.minRssiDifferenceToSwitch}
              onChange={(e) =>
                setConfig({
                  ...config,
                  minRssiDifferenceToSwitch: parseInt(e.target.value),
                })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              RSSI improvement required to switch dominant gateway (prevents
              flapping)
            </p>
          </div>
        </div>
      </div>

      {/* RSSI-Based Distance */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          📏 RSSI-Based Distance Calculation
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Enable RSSI Distance</p>
              <p className="text-sm text-gray-600">
                Calculate distance from gateway based on signal strength
              </p>
            </div>
            <button
              onClick={() =>
                setConfig({
                  ...config,
                  useRssiBasedDistance: !config.useRssiBasedDistance,
                })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.useRssiBasedDistance ? "bg-green-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.useRssiBasedDistance
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {config.useRssiBasedDistance && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance Model
                </label>
                <select
                  value={config.rssiDistanceModel}
                  onChange={(e) =>
                    setConfig({ ...config, rssiDistanceModel: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="linear">Linear</option>
                  <option value="logarithmic">Logarithmic (Recommended)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Near RSSI: {config.nearRssi} dBm
                  </label>
                  <input
                    type="range"
                    min="-60"
                    max="-20"
                    value={config.nearRssi}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        nearRssi: parseInt(e.target.value),
                      })
                    }
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Strong signal threshold
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Far RSSI: {config.farRssi} dBm
                  </label>
                  <input
                    type="range"
                    min="-90"
                    max="-60"
                    value={config.farRssi}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        farRssi: parseInt(e.target.value),
                      })
                    }
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">Weak signal threshold</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Directional Positioning */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          🧭 Directional Positioning
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">
                Enable Directional Positioning
              </p>
              <p className="text-sm text-gray-600">
                Use secondary gateways to determine direction
              </p>
            </div>
            <button
              onClick={() =>
                setConfig({
                  ...config,
                  useDirectionalPositioning: !config.useDirectionalPositioning,
                })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.useDirectionalPositioning
                  ? "bg-green-600"
                  : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.useDirectionalPositioning
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {config.useDirectionalPositioning && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Gateway Weight:{" "}
                  {config.secondaryGatewayWeight.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.secondaryGatewayWeight}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      secondaryGatewayWeight: parseFloat(e.target.value),
                    })
                  }
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Influence of 2nd strongest gateway on direction
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tertiary Gateway Weight:{" "}
                  {config.tertiaryGatewayWeight.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.tertiaryGatewayWeight}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      tertiaryGatewayWeight: parseFloat(e.target.value),
                    })
                  }
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Influence of 3rd strongest gateway on direction
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 disabled:bg-gray-300 transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
          >
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DominantConfigEditor;
